import React, { useEffect, useMemo } from "react";
import { Box } from "@mui/material";

import { useDisplayField } from "../../../hooks/useDisplayField";
import { ControlledAutocomplete, FieldControl } from "../../../shared/components";
import { useGetJeopardyStakeholders } from "../../../shared/hooks";

interface QueryFiltersProps {
    filter: any;
    handleUpdate: (key: string, value: any) => void;
    isPendingPhase: boolean;
    phaseOptions: { id: string; name: string }[];
    isProjectsearchDisabled: boolean;
    hiddenFilters?: string[];
};

export const QueryFilters: React.FC<QueryFiltersProps> = ({ filter, handleUpdate, isPendingPhase, phaseOptions, isProjectsearchDisabled, hiddenFilters }) => {

    const { getFilterOptions } = useDisplayField([]);

    const payload = () => {
        if (filter.type === "Project" && filter.project?.id) {
            return { project_id: filter.project.id };
        } else if (filter.type === "Programme" && filter.programme?.id) {
            return { programme_id: filter.programme.id };
        } else if (filter.type === "Portfolio" && filter.portfolio?.id) {
            return { portfolio_id: filter.portfolio.id };
        }
        return {};
    };

    const { data: stakeholders, isLoading: isLoadingUsers, refetch: refetchStakeHolders } = useGetJeopardyStakeholders({ params: payload() });

    const userOptions = useMemo(() => {
        return stakeholders?.data?.flatMap((role: any) =>
            role?.users?.map((user: any) => ({
                id: user.user_id,
                name: user.user_name,
            }))
        ) || [];
    }, [stakeholders]);

    useEffect(() => {
        const currentProjectId = (filter.type === "Project" ? filter.project?.id : filter.type === "Programme" ? filter.programme?.id : filter.type === "Portfolio" ? filter.portfolio?.id : undefined);
        if (currentProjectId) {
            refetchStakeHolders();
        }
    }, [filter.type, filter.project?.id, filter.programme?.id, filter.portfolio?.id, refetchStakeHolders]);

    return (
        <Box display={'flex'} flexDirection={'column'} gap={2} p={2}>

            <FieldControl
                keyName={'priority'}
                value={filter.priority}
                label={"Query Priority"}
                variant={"select"}
                options={getFilterOptions('QUERY_PRIORITY').map((value) => {
                    return {
                        label: value.display_text,
                        value: value.key
                    }
                })}
                onChange={handleUpdate}
                multiSelect
            />

            <FieldControl
                keyName={'status'}
                value={filter.status}
                label={"Query Status"}
                variant={"select"}
                options={getFilterOptions('QUERY_STATUS').map((value) => {
                    return {
                        label: value.display_text,
                        value: value.key
                    }
                })}
                onChange={handleUpdate}
                multiSelect
            />

            <FieldControl
                keyName={'category'}
                value={filter.category}
                label={"Query Category"}
                variant={"select"}
                options={getFilterOptions('QUERY_CATEGORY').map((value) => {
                    return {
                        label: value.display_text,
                        value: value.key
                    }
                })}
                onChange={handleUpdate}
                multiSelect
            />

            <ControlledAutocomplete
                type="PROJECT"
                value={filter.project}
                onSelect={(values) => handleUpdate("project", values)}
                label="Search project"
                placeholder="Search project"
                disabled={isProjectsearchDisabled}
            />

            {
                hiddenFilters?.includes('portfolio') &&
                <ControlledAutocomplete
                    type="PORTFOLIO"
                    multiSelect
                    value={filter.portfolio}
                    onSelect={(values) => handleUpdate("portfolio", values)}
                    label="Search portfolio"
                    placeholder="Search portfolio"
                />
            }

            {
                hiddenFilters?.includes('programme') &&
                <ControlledAutocomplete
                    type="PROGRAMME"
                    multiSelect
                    value={filter.programme}
                    onSelect={(values) => handleUpdate("programme", values)}
                    label="Search programme"
                    placeholder="Search programme"
                />
            }

            <FieldControl
                keyName={'phase'}
                value={filter.phase}
                label={"Select Project Phase"}
                variant={"select"}
                options={
                    phaseOptions.map((value: { id: string, name: string; }) => {
                        return {
                            value: value.id, label: value.name
                        }
                    })
                }
                onChange={handleUpdate}
                loading={isPendingPhase}
                multiSelect
            />

            <FieldControl
                keyName={'assigned'}
                value={filter.assigned}
                label={"Assigned to"}
                variant={"select"}
                options={userOptions}
                loading={isLoadingUsers}
                onChange={handleUpdate}
                multiSelect
            />

        </Box>
    )
}